import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { ErrandCardSkeleton } from '../components/UI/LoadingSkeleton';
import EmptyState from '../components/UI/EmptyState';
import PaymentCard from '../components/PaymentCard';
import { getErrorMessage } from '../utils/errorMessages';
import { showError } from '../utils/toast';

interface PaymentTransaction {
  transaction_id: string;
  errand_title: string;
  amount: number;
  service_fee: number;
  driver_earnings: number;
  payment_method: string;
  payment_status: string;
  errand_status: string;
  errand_type: string;
  customer_name: string;
  driver_name: string;
  transaction_date: string;
  errand_created_at: string;
  completed_at: string | null;
}

const PaymentHistory: React.FC = () => {
  const { user } = useAuth();
  const [transactions, setTransactions] = useState<PaymentTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'completed' | 'failed' | 'refunded'>('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (user) {
      fetchPaymentHistory();
    }
  }, [user, filter]);

  const fetchPaymentHistory = async () => {
    try {
      setLoading(true);

      // Get current user ID first
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) {
        setTransactions([]);
        setLoading(false);
        return;
      }

      // Try to query payment_history view first, fallback to direct errands query
      // IMPORTANT: Always filter by user_id or driver_id to ensure users only see their own payments
      let query = supabase
        .from('payment_history')
        .select('*')
        .or(`user_id.eq.${authUser.id},driver_id.eq.${authUser.id}`)
        .order('transaction_date', { ascending: false });

      // Apply filter
      if (filter !== 'all') {
        query = query.eq('payment_status', filter);
      }

      let { data, error } = await query;

      // Additional safety check: filter results client-side to ensure only user's payments are shown
      if (data && !error) {
        data = data.filter((t: any) =>
          t.user_id === authUser.id || t.driver_id === authUser.id
        );
      }

      // If view doesn't exist, query errands table directly
      if (error && (error.code === '42P01' || error.message?.includes('does not exist'))) {
        console.log('Payment history view not found, querying errands directly...');

        // Get current user ID
        const { data: { user: authUser } } = await supabase.auth.getUser();
        if (!authUser) {
          setTransactions([]);
          setLoading(false);
          return;
        }

        // Query errands directly with proper joins - filter by current user only
        let errandsQuery = supabase
          .from('errands')
          .select(`
            id,
            user_id,
            driver_id,
            title,
            total_cost,
            service_fee,
            driver_earnings,
            payment_method,
            payment_status,
            status,
            created_at,
            completed_at,
            updated_at,
            errand_types (name)
          `)
          .or(`user_id.eq.${authUser.id},driver_id.eq.${authUser.id}`)
          .order('updated_at', { ascending: false });

        if (filter !== 'all') {
          errandsQuery = errandsQuery.eq('payment_status', filter);
        }

        const { data: errandsData, error: errandsError } = await errandsQuery;

        if (errandsError) {
          console.error('Error querying errands:', errandsError);
          throw errandsError;
        }

        // Get user profiles separately if needed
        const userIds = new Set<string>();
        const driverIds = new Set<string>();
        (errandsData || []).forEach((e: any) => {
          if (e.user_id) userIds.add(e.user_id);
          if (e.driver_id) driverIds.add(e.driver_id);
        });

        // Fetch profiles
        const { data: profilesData } = await supabase
          .from('profiles')
          .select('id, full_name')
          .in('id', Array.from(new Set([...userIds, ...driverIds])));

        const profilesMap = new Map((profilesData || []).map((p: any) => [p.id, p.full_name]));

        // Transform errands data to match payment_history structure
        const transformedData = (errandsData || []).map((e: any) => ({
          transaction_id: e.id,
          user_id: e.user_id,
          driver_id: e.driver_id,
          errand_title: e.title,
          amount: e.total_cost || 0,
          service_fee: e.service_fee || 0,
          driver_earnings: e.driver_earnings || 0,
          payment_method: e.payment_method,
          payment_status: e.payment_status,
          errand_status: e.status,
          errand_created_at: e.created_at,
          completed_at: e.completed_at,
          transaction_date: e.updated_at,
          errand_type: e.errand_types?.name || 'N/A',
          customer_name: profilesMap.get(e.user_id) || 'N/A',
          driver_name: e.driver_id ? (profilesMap.get(e.driver_id) || null) : null
        }));

        setTransactions(transformedData);
      } else {
        if (error) throw error;
        setTransactions(data || []);
      }
    } catch (error) {
      console.error('Error fetching payment history:', error);
      showError(getErrorMessage(error, 'Failed to load payment history'));
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-BW', {
      style: 'currency',
      currency: 'BWP'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-BW', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };



  const filteredTransactions = transactions.filter(transaction => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      transaction.errand_title.toLowerCase().includes(query) ||
      transaction.errand_type.toLowerCase().includes(query) ||
      transaction.driver_name?.toLowerCase().includes(query) ||
      transaction.payment_method.toLowerCase().includes(query)
    );
  });

  const totalAmount = filteredTransactions
    .filter(t => t.payment_status === 'completed')
    .reduce((sum, t) => sum + t.amount, 0);

  const generateReceipt = (transaction: PaymentTransaction) => {
    const receiptContent = `
STASHIT PAYMENT RECEIPT

Transaction Details:
-------------------
Transaction ID: ${transaction.transaction_id}
Date: ${formatDate(transaction.transaction_date)}
Status: ${transaction.payment_status.charAt(0).toUpperCase() + transaction.payment_status.slice(1)}

Service Details:
---------------
Errand Title: ${transaction.errand_title}
Errand Type: ${transaction.errand_type}
Payment Method: ${transaction.payment_method.replace('_', ' ')}

${transaction.driver_name ? `Driver: ${transaction.driver_name}` : ''}

Amount Breakdown:
----------------
Total Amount: ${formatCurrency(transaction.amount)}
${transaction.service_fee > 0 ? `Service Fee: ${formatCurrency(transaction.service_fee)}` : ''}
${transaction.driver_earnings > 0 ? `Driver Earnings: ${formatCurrency(transaction.driver_earnings)}` : ''}

Security Information:
-------------------
This transaction is secured by SSL encryption
Verified by Stashit Payment System

Thank you for using Stashit!
For support: support@stashit.co.bw
    `.trim();

    const blob = new Blob([receiptContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Stashit_Receipt_${transaction.transaction_id.slice(-8)}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
        {/* Header */}
        <div className="bg-white shadow-sm border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="text-center">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Payment History</h1>
              <p className="text-gray-600">Track and manage all your payment transactions</p>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid gap-6 md:grid-cols-1 lg:grid-cols-2 xl:grid-cols-3">
            <ErrandCardSkeleton />
            <ErrandCardSkeleton />
            <ErrandCardSkeleton />
            <ErrandCardSkeleton />
            <ErrandCardSkeleton />
            <ErrandCardSkeleton />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Payment History</h1>
            <p className="text-gray-600">Track and manage all your payment transactions</p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Transactions</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{transactions.length}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-lg">
                <span className="text-2xl">📊</span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Amount Paid</p>
                <p className="text-2xl font-bold text-green-600 mt-1">{formatCurrency(totalAmount)}</p>
              </div>
              <div className="p-3 bg-green-100 rounded-lg">
                <span className="text-2xl">💰</span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Completed Payments</p>
                <p className="text-2xl font-bold text-blue-600 mt-1">
                  {transactions.filter(t => t.payment_status === 'completed').length}
                </p>
              </div>
              <div className="p-3 bg-blue-100 rounded-lg">
                <span className="text-2xl">✅</span>
              </div>
            </div>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Search */}
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">Search Transactions</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <input
                  type="text"
                  placeholder="Search by errand title, type, or driver..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                />
              </div>
            </div>

            {/* Status Filter */}
            <div className="lg:w-64">
              <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Status</label>
              <div className="flex flex-wrap gap-2">
                {(['all', 'completed', 'failed', 'refunded'] as const).map((status) => (
                  <button
                    key={status}
                    onClick={() => setFilter(status)}
                    className={`px-4 py-2 rounded-lg font-medium transition-all text-sm ${
                      filter === status
                        ? 'bg-blue-600 text-white shadow-md transform scale-105'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:shadow-sm'
                    }`}
                  >
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Enhanced Transactions List with PaymentCard */}
        {filteredTransactions.length === 0 ? (
          <EmptyState
            icon="💰"
            title="No Payment History"
            message={
              searchQuery
                ? `No transactions match "${searchQuery}"`
                : "You don't have any payment transactions yet."
            }
            actionLabel={searchQuery ? "Clear Search" : "Request Errand"}
            actionPath={searchQuery ? undefined : "/errand/request"}
            onAction={searchQuery ? () => setSearchQuery('') : undefined}
          />
        ) : (
          <div className="grid gap-6 md:grid-cols-1 lg:grid-cols-2 xl:grid-cols-3">
            {filteredTransactions.map((transaction) => (
              <PaymentCard
                key={transaction.transaction_id}
                payment={transaction}
                onViewDetails={() => {
                  // Could implement detailed view modal here
                  console.log('View details for:', transaction.transaction_id);
                }}
                onDownloadReceipt={() => generateReceipt(transaction)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentHistory;
